import { useEffect, useRef, useState } from 'react';
import { Button, Icon } from '@/components/primitives';
import { exportCsv, exportXlsx, type ExportColumn } from '@/lib/exportUtils';

interface Props<T> {
  rows: T[];
  columns: ExportColumn<T>[];
  filename: string;
  label?: string;
}

export function ExportButton<T>({ rows, columns, filename, label = 'Xuất' }: Props<T>) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <Button
        variant="outline"
        size="sm"
        icon="download"
        iconRight="chevron-down"
        onClick={() => setOpen((v) => !v)}
        disabled={rows.length === 0}
      >
        {label} ({rows.length})
      </Button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            minWidth: 180,
            background: '#fff',
            border: '1px solid var(--bx-border-muted)',
            borderRadius: 'var(--bx-radius-md)',
            boxShadow: 'var(--bx-shadow-pop)',
            zIndex: 60,
            padding: 4,
          }}
        >
          <ExportOption
            icon="excel"
            label="XLSX (Excel)"
            onClick={() => {
              exportXlsx(rows, columns, filename);
              setOpen(false);
            }}
          />
          <ExportOption
            icon="external"
            label="CSV"
            onClick={() => {
              exportCsv(rows, columns, filename);
              setOpen(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

function ExportOption({
  icon,
  label,
  onClick,
}: {
  icon: 'excel' | 'external';
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 10px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        borderRadius: 6,
        color: 'var(--bx-text-dark)',
        fontFamily: 'var(--bx-font-sans)',
        fontSize: 12.5,
        textAlign: 'left',
      }}
    >
      <Icon name={icon} size={12} style={{ color: 'var(--bx-text-muted)' }} />
      {label}
    </button>
  );
}
